<template>
  <div 
    :id="id" 
    :name="name" 
    data-test-id="editor-container"
    :class="[$style['container'], { [$style[custom ? 'cutom-border' : 'error-border']]: Boolean(errorMessage) || bannedWordIsPresent }, props.class, [$style[custom ? 'border-radius' : '']]]"
  >
    <editor-toolbar 
      v-if="isBundle && editorRef" 
      :editor="editorRef" 
      :class="toolbarClass" 
    />
    <default-toolbar 
      v-else-if="editorRef" 
      :editor="editorRef" 
      :class="toolbarClass" 
    />
    <editor-content :editor="editorRef" />
  </div>
  <div 
    v-if="(showErrorMessage && !bannedWordIsPresent && !warningWordIsPresent) || importantErrorMessages"
    class="mt-error" 
    :class="errorClass"
  >
    {{ errorMessage }}
  </div>
  <p 
    v-if="bannedWordIsPresent && showBannedWordError" 
    class="mt-error" 
    :class="errorClass"
  >
    {{ $t('pages.profile.foundry_profile.banned_word_error_message') }}
  </p>
  <p 
    v-if="warningWordIsPresent && showWarningWordError" 
    class="mt-error warning" 
    :class="errorClass"
    :style="{ color: '#DC5800' }"
  >
    {{ $t('pages.profile.foundry_profile.warning_word_error_message', { words: warningWords.join('", "') }) }}
  </p>
  <div
    v-if="showCharacterCount" 
    data-qa-id="editor-container-text-counter"
    :class="[$style.characterCountClass, errorMessage ? $style.errorCharacterCountText : '']"
  >
    {{ counter }}/{{ showUpperCharacterCount ? maxCharacterCountUpperLimit : maxCharacterCount }}
  </div>
</template>

<script setup>
import { useCssModule, onBeforeUnmount,onBeforeUpdate, watch, ref, computed } from 'vue'
import { useStore } from 'vuex';
import Link from '@tiptap/extension-link'
import StarterKit from '@tiptap/starter-kit'
import Strike from '@tiptap/extension-strike'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { useField } from 'vee-validate'
import { clsx } from 'clsx'
import Misc from 'src/utils/helper/misc'

import EditorToolbar from './EditorToolbar.vue'
import defaultToolbar from './defaultToolbar.vue'
const store = useStore();

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  showErrorMessage: {
    type: Boolean,
    default: true
  },
  showBannedWordError: {
    type: Boolean,
    default: true
  },
  showWarningWordError: {
    type: Boolean,
    default: true
  },
  dataQaId: {
    type: String,
    default: 'editor-container'
  },
  class: {
    type: [String, Object, Array],
    default: ''
  },
  toolbarClass: {
    type: [String, Object, Array],
    default: ''
  },
  editorClass: {
    type: [String, Object, Array],
    default: ''
  },
  errorClass: {
    type: [String, Object, Array],
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  rules: {
    type: String,
    default: ''
  },
  custom: {
    type: Boolean,
    default: false
  },
  isBundle:{
    type:Boolean,
    default:false
  },
  bannedWord: {
    type: Boolean,
    default: false
  },
  showCharacterCount: {
    type: Boolean,
    default: false
  },
  maxCharacterCount: {
    type: String,
    default: '600'
  },
  maxCharacterCountUpperLimit: {
    type: String,
    default: '64000'
  },
  moduleName: {
    type: String,
    default: ''
  },
  foundryId: {
    type: String,
    default: ''
  },
  importantErrorMessages: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
    default: ''
  },
  runDetectBannedWords: {
    type: Boolean,
    default: false
  },
  checkLengthOnCreate: {
    type: Boolean,
    default: false
  }
})


const emits = defineEmits([
  'update:modelValue', 
  'ready', 
  'blur', 
  'focus',
  'update-description',
  'update-banned-word',
  'update-warning-existence',
  'update-warning-words',
  'update-detect'
])

const $style = useCssModule()

const { errorMessage, validate, value: textEditorValue } = useField(() => props.name, props.rules, {
  initialValue: props.modelValue,
  valueProp: props.modelValue
})

const getEditorContent = editor => (editor?.isEmpty ? '' : editor?.getHTML())

const counter = ref(props.modelValue.length)
const bannedWordIsPresent = ref(false)
const warningWordIsPresent = ref(false)
const warningWords = ref([])
const previousWarningWords = ref([]);
const previousBannedWords = ref([]);

const showUpperCharacterCount = computed(() => {
  return typeof props.maxCharacterCount === 'string' && props.maxCharacterCount.includes('+') && counter.value >= props.maxCharacterCount.split('+')[0];
})

let detectBannedWordCounter = 0;
const maxDetectBannedWordCalls = 4;

const handleBlur = (fieldName) => {
  const bannedWordInfo = Misc.strikeThroughBadWords(props.description , store , fieldName, props.moduleName , props.foundryId, previousWarningWords.value, previousBannedWords.value)
  if(props.showBannedWordError)
  bannedWordIsPresent.value = bannedWordInfo.bannedWordExist

  warningWordIsPresent.value = bannedWordInfo.warningWordExist
  warningWords.value = bannedWordInfo.warningWordMatches
  previousWarningWords.value = bannedWordInfo.warningWordMatches;
  previousBannedWords.value = bannedWordInfo.banned;
  emits('update-description', bannedWordInfo.description)
  emits('update-banned-word', bannedWordInfo.bannedWordExist)
  emits('update-warning-existence', bannedWordInfo.warningWordExist)
  emits('update-warning-words', bannedWordInfo.warningWordMatches)
  detectBannedWordCounter++;
  if (detectBannedWordCounter <= maxDetectBannedWordCalls) {
    emits('update-detect', false);
    detectBannedWordCounter = 0;
  }
}

const editorRef = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    Link.configure({
      openOnClick: false
    }),
    Strike
  ],
  onCreate: ({ editor }) => {
    if (props.checkLengthOnCreate) {
      const strippedString = getEditorContent(editor).replace(/<\/?[\w\s="'/:;#.-]+>/gi, '')
      const stringWithoutSpaces = strippedString.trim()
      counter.value = stringWithoutSpaces.length
    }
    emits('ready', editor)
  },
  onBlur: ({ event, editor }) => {
    handleBlur('text-editor')
    emits('blur', event )
    validate()
  },
  onFocus: ({ event }) => {
    emits('focus', event)
  },
  onUpdate: ({ editor }) => {
    emits('update:modelValue', getEditorContent(editor))
    textEditorValue.value =  getEditorContent(editor)
    if (getEditorContent(editor).length > 0) {
      const strippedString = getEditorContent(editor).replace(/<\/?[\w\s="'/:;#.-]+>/gi, '')
      const stringWithoutSpaces = strippedString.trim()
      counter.value = stringWithoutSpaces.length
      if (props.maxCharacterCount?.includes('+') && counter.value >= props.maxCharacterCount.split('+')[0]) {
        showUpperCharacterCount.value = true
      }
      else {
        showUpperCharacterCount.value = false
      }
    }
    else {
      counter.value = 0
    }
    validate()
  },
  editorProps: {
    attributes: {
      class: clsx($style['editor-content'], props.editorClass),
      'data-test-id': 'editor-content'
    }
  }
})

watch(() => props.modelValue, (newValue) => {
  if (!editorRef.value) return; 
  const isSame = newValue == editorRef.value?.getHTML();
    if (isSame) {
      editorRef.value?.commands.unsetStrike()
      return;
    }

    editorRef.value?.commands.setContent(newValue, false)
    editorRef.value?.commands.unsetStrike()
    const valueWithoutHtml = newValue.replace(/<\/?[\w\s="'/:;#.-]+>/gi, '')
    counter.value = valueWithoutHtml.length;
    textEditorValue.value = newValue;
})

watch(() => props.runDetectBannedWords, (newValue) => {
  if (newValue) {
    handleBlur('text-editor')
  }
});

onBeforeUpdate(() => {
  if (!(editorRef?.value)) return

  editorRef.value.setOptions({
    editorProps: {
      attributes: {
        class: clsx($style['editor-content'], props.editorClass),
        'data-test-id': 'editor-content'
      }
    }
  })
})

onBeforeUnmount(() => {
  editorRef.value?.destroy()
})
</script>

<style lang="scss" module>
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/mfpfonts/fonts.scss';

.container {
  outline: 1px solid #ccc;
  width: 100%;
  font-family: 'Helvetica Now Display W05 Regular' , sans-serif;
  margin-bottom: 0.25rem;
}

s {
  text-decoration: underline;
  text-underline-offset: -39%;
  text-decoration-skip-ink: none;
  color: map-get($colors, 'red');
}

.error-border {
  border: 1px solid map-get($colors, 'red');
  border-left-width: 0.375rem;
  width: 100%;
  outline: none;
}

.cutom-border {
  border: 1px solid map-get($colors, 'red');
  width: 100%;
  outline: none;
}

.border-radius{
  border-radius: 10px;
  padding: 0;
}

.editor-content {
  padding: 12px 15px;
  outline: revert;
  min-height: 200px;

  &:focus {
    outline: none !important;
  }

}

.characterCountClass {
  @extend .mfp-HelveticaNowMTText-Regular-13;
  color: #576579;
  margin-top: 0.5rem;
}
.errorCharacterCountText{
  color: map-get($colors, 'red');
}

.editor-content ol,
.editor-content ul {
  margin: 0;
  padding-left: 0.9rem;
}

.editor-content strong,
.editor-content em,
.editor-content i {
  line-height: normal;
  vertical-align: baseline;
}
</style>
